import { Plus, X, Trash2 } from "lucide-react";
import {
  PERSONALIZATION_LIMITS,
  type PersonalizationQuestion,
  type PersonalizationQuestionType,
} from "@erc/shared";

interface Props {
  questions: PersonalizationQuestion[];
  onChange: (questions: PersonalizationQuestion[]) => void;
  /** tailwind focus ring accent, e.g. "indigo" (builder) or "blue" (templates) */
  accent?: string;
}

const TYPE_LABELS: Record<PersonalizationQuestionType, string> = {
  text_input: "Metin",
  dropdown: "Açılır liste",
  unlabeled_upload: "Dosya yükleme",
  labeled_upload: "Etiketli dosya yükleme",
};

function makeDefaultQuestion(type: PersonalizationQuestionType): PersonalizationQuestion {
  const base: PersonalizationQuestion = {
    question_type: type,
    question_text: "",
    required: true,
  };
  if (type === "text_input") base.max_allowed_characters = 50;
  if (type === "dropdown") base.options = [{ label: "" }, { label: "" }];
  if (type === "unlabeled_upload") base.max_allowed_files = 1;
  if (type === "labeled_upload") {
    base.max_allowed_files = 2;
    base.options = [{ label: "" }, { label: "" }];
  }
  return base;
}

export function PersonalizationEditor({ questions, onChange, accent = "indigo" }: Props) {
  const ring = `focus:ring-${accent}-200`;
  const uploadCount = questions.filter(
    (q) => q.question_type === "unlabeled_upload" || q.question_type === "labeled_upload"
  ).length;
  const atMax = questions.length >= PERSONALIZATION_LIMITS.maxQuestions;

  function update(i: number, patch: Partial<PersonalizationQuestion>) {
    onChange(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  }

  function changeType(i: number, type: PersonalizationQuestionType) {
    // rebuild with sensible defaults but preserve the question text + required flag
    const prev = questions[i];
    const next = makeDefaultQuestion(type);
    next.question_text = prev.question_text;
    next.required = prev.required;
    onChange(questions.map((q, idx) => (idx === i ? next : q)));
  }

  function addQuestion() {
    if (atMax) return;
    onChange([...questions, makeDefaultQuestion("text_input")]);
  }

  function removeQuestion(i: number) {
    onChange(questions.filter((_, idx) => idx !== i));
  }

  function updateOption(qi: number, oi: number, label: string) {
    const q = questions[qi];
    const options = (q.options ?? []).map((o, idx) => (idx === oi ? { label } : o));
    update(qi, { options });
  }

  function addOption(qi: number) {
    const q = questions[qi];
    update(qi, { options: [...(q.options ?? []), { label: "" }] });
  }

  function removeOption(qi: number, oi: number) {
    const q = questions[qi];
    update(qi, { options: (q.options ?? []).filter((_, idx) => idx !== oi) });
  }

  return (
    <div className="space-y-3">
      {questions.map((q, i) => {
        const isUpload = q.question_type === "unlabeled_upload" || q.question_type === "labeled_upload";
        const isDuplicateUpload = isUpload && uploadCount > 1;
        return (
          <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-gray-400">Soru {i + 1}</span>
              <select
                value={q.question_type}
                onChange={(e) => changeType(i, e.target.value as PersonalizationQuestionType)}
                className={`flex-1 text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:ring-2 ${ring} focus:outline-none`}
              >
                {(Object.keys(TYPE_LABELS) as PersonalizationQuestionType[]).map((t) => (
                  <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-[11px] text-gray-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={q.required}
                  onChange={(e) => update(i, { required: e.target.checked })}
                  className="rounded border-gray-300"
                />
                Zorunlu
              </label>
              <button
                type="button"
                onClick={() => removeQuestion(i)}
                className="text-gray-400 hover:text-red-500 p-1"
                title="Soruyu sil"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            <div>
              <input
                type="text"
                value={q.question_text}
                maxLength={PERSONALIZATION_LIMITS.questionTextMax}
                onChange={(e) => update(i, { question_text: e.target.value })}
                placeholder="Soru başlığı (örn: İsim, Renk seçimi)"
                className={`w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-2 ${ring} focus:outline-none`}
              />
              <div className="text-[10px] text-gray-400 text-right mt-0.5">
                {q.question_text.length}/{PERSONALIZATION_LIMITS.questionTextMax}
              </div>
            </div>

            {q.question_type === "text_input" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500 block mb-0.5">Maks. karakter</label>
                  <input
                    type="number"
                    min={PERSONALIZATION_LIMITS.charCountMin}
                    max={PERSONALIZATION_LIMITS.charCountMax}
                    value={q.max_allowed_characters ?? 50}
                    onChange={(e) => update(i, { max_allowed_characters: Number(e.target.value) })}
                    className={`w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-2 ${ring} focus:outline-none`}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 block mb-0.5">Talimat (ops.)</label>
                  <input
                    type="text"
                    value={q.instructions ?? ""}
                    maxLength={PERSONALIZATION_LIMITS.instructionsMax}
                    onChange={(e) => update(i, { instructions: e.target.value })}
                    placeholder="Örn: Lütfen istediğiniz ismi yazın"
                    className={`w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-2 ${ring} focus:outline-none`}
                  />
                </div>
              </div>
            )}

            {q.question_type === "dropdown" && (
              <div className="space-y-1.5">
                <label className="text-[10px] text-gray-500 block">Seçenekler ({PERSONALIZATION_LIMITS.dropdownOptionsMin}-{PERSONALIZATION_LIMITS.dropdownOptionsMax})</label>
                {(q.options ?? []).map((o, oi) => (
                  <div key={oi} className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={o.label}
                      maxLength={PERSONALIZATION_LIMITS.dropdownLabelMax}
                      onChange={(e) => updateOption(i, oi, e.target.value)}
                      placeholder={`Seçenek ${oi + 1}`}
                      className={`flex-1 text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-2 ${ring} focus:outline-none`}
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(i, oi)}
                      className="text-gray-400 hover:text-red-500 p-1"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {(q.options?.length ?? 0) < PERSONALIZATION_LIMITS.dropdownOptionsMax && (
                  <button
                    type="button"
                    onClick={() => addOption(i)}
                    className="text-[11px] text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Seçenek ekle
                  </button>
                )}
              </div>
            )}

            {isUpload && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-gray-500 block mb-0.5">
                      Maks. dosya ({q.question_type === "labeled_upload" ? PERSONALIZATION_LIMITS.labeledUploadFilesMin : PERSONALIZATION_LIMITS.uploadFilesMin}-{PERSONALIZATION_LIMITS.uploadFilesMax})
                    </label>
                    <input
                      type="number"
                      min={q.question_type === "labeled_upload" ? PERSONALIZATION_LIMITS.labeledUploadFilesMin : PERSONALIZATION_LIMITS.uploadFilesMin}
                      max={PERSONALIZATION_LIMITS.uploadFilesMax}
                      value={q.max_allowed_files ?? 1}
                      onChange={(e) => update(i, { max_allowed_files: Number(e.target.value) })}
                      className={`w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-2 ${ring} focus:outline-none`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 block mb-0.5">Talimat (ops.)</label>
                    <input
                      type="text"
                      value={q.instructions ?? ""}
                      maxLength={PERSONALIZATION_LIMITS.instructionsMax}
                      onChange={(e) => update(i, { instructions: e.target.value })}
                      className={`w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-2 ${ring} focus:outline-none`}
                    />
                  </div>
                </div>
                {q.question_type === "labeled_upload" && (
                  <div className="space-y-1.5">
                    <label className="text-[10px] text-gray-500 block">Dosya etiketleri (dosya sayısı kadar olmalı)</label>
                    {(q.options ?? []).map((o, oi) => (
                      <div key={oi} className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={o.label}
                          maxLength={PERSONALIZATION_LIMITS.labeledLabelMax}
                          onChange={(e) => updateOption(i, oi, e.target.value)}
                          placeholder={`Etiket ${oi + 1}`}
                          className={`flex-1 text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-2 ${ring} focus:outline-none`}
                        />
                        <button type="button" onClick={() => removeOption(i, oi)} className="text-gray-400 hover:text-red-500 p-1">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => addOption(i)} className="text-[11px] text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
                      <Plus className="h-3 w-3" /> Etiket ekle
                    </button>
                  </div>
                )}
                {isDuplicateUpload && (
                  <p className="text-[11px] text-red-500">Liste başına yalnızca 1 dosya yükleme sorusu olabilir.</p>
                )}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addQuestion}
        disabled={atMax}
        className={`w-full text-xs border border-dashed rounded-lg px-3 py-2 inline-flex items-center justify-center gap-1.5 transition-colors ${
          atMax
            ? "border-gray-200 text-gray-300 cursor-not-allowed"
            : "border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700"
        }`}
      >
        <Plus className="h-3.5 w-3.5" /> Soru Ekle ({questions.length}/{PERSONALIZATION_LIMITS.maxQuestions})
      </button>
    </div>
  );
}
